import { css, useTheme } from "@emotion/react";
import styled from "@emotion/styled";
import BookmarkIcon from "@mui/icons-material/BookmarkTwoTone";
import OrgIcon from "@mui/icons-material/FolderSharedTwoTone";
import HomeIcon from "@mui/icons-material/HomeRounded";
import ArchiveIcon from "@mui/icons-material/Inventory2TwoTone";
import ManageSearchIcon from "@mui/icons-material/ManageSearchTwoTone";
import PublicationsIcon from "@mui/icons-material/MenuBookTwoTone";
import FrjMenuIcon from "@assets/frjMenuIcon.svg";
import UsersIcon from "@mui/icons-material/PeopleAltTwoTone";
import ShelvesIcon from "@mui/icons-material/Shelves";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import LoginIcon from "@mui/icons-material/Login";
import AnnotationIcon from "@mui/icons-material/StickyNote2TwoTone";
import { NavLink, useLocation } from "react-router";
import { pxToRem } from "../../design/utils";
import { useMemo } from "react";

const StyledNav = styled(NavLink)`
  all: unset;
  cursor: pointer;
  display: flex;
  justify-content: start;
  align-items: center;
  padding-left: ${({ theme }) => theme.spacing(2)};
  border-radius: 8px;
  height: ${pxToRem(44)};
  color: ${({ theme }) => theme.colours.primary.dark._10};
  &.active {
    ${({ theme }) => theme.layers.relief.primaryDark_10.shadow};
    color: ${({ theme }) => theme.colours.primary.light._20};
  }
  max-width: 100%;
  overflow: hidden;
  @media (max-width: ${({ theme }) => theme.breakpoints.values.lg}px) {
    padding-left: ${({ theme }) => theme.spacing(1)};
  }
`;

interface MenuItemProps {
  to: string;
  label: string;
  alsoActiveWhen?: (pathname: string) => boolean;
}

export const MenuItem = ({ to, label, alsoActiveWhen }: MenuItemProps) => {
  const { pathname } = useLocation();
  const theme = useTheme();
  const icon = (() => {
    switch (label) {
      case "Home":
        return HomeIcon;
      case "My Shelf":
        return ShelvesIcon;
      case "Highlights & Notes":
        return AnnotationIcon;
      case "Bookmarks":
        return BookmarkIcon;
      case "Archive":
        return ArchiveIcon;
      case "Users":
        return UsersIcon;
      case "Publications":
        return PublicationsIcon;
      case "Search Suggestions":
        return ManageSearchIcon;
      case "Organisations":
        return OrgIcon;
      case "Login Page Content":
        return LoginIcon;
      case "FRJ Tag Filter":
        return LocalOfferIcon;
      case "FRJ Articles":
        return FrjMenuIcon;
      default:
        return HomeIcon;
    }
  })();
  const forceActive = alsoActiveWhen?.(pathname) ?? false;

  const IconComponent = useMemo(() => {
    return typeof icon === "string"
      ? () => (
          <img
            src={icon}
            alt={label}
            height={"24px"}
            css={css`
              margin-right: 0.45rem;
              margin-left: -0.25rem;
              filter: brightness(0) saturate(100%) invert(11%) sepia(36%)
                saturate(4005%) hue-rotate(219deg) brightness(90%) contrast(88%);
              .active & {
                filter: brightness(0) invert(1);
              }
            `}
          />
        )
      : icon;
  }, [icon, label]);

  return (
    <StyledNav to={to} className={forceActive ? "active" : undefined}>
      <IconComponent
        sx={{
          marginRight: theme.spacing(1.5),
        }}
      />
      <p
        style={{
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          minWidth: "0",
          flexShrink: "1",
          ...theme.customTypography.title._2,
        }}
      >
        {label}
      </p>
    </StyledNav>
  );
};
